import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { SiteConfigManager, useApplicationState, useCasinoNewState } from '@atlas/core';
import { CASINO_NAVIGATION_TYPES, CasinoNavigationMenu } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import useTranslate from 'common/I18n/Interpreter';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN } from 'common/utils';
import { useCasinoFilterState } from 'components/Casino/CasinoFilterContext';
import CasinoSearch from 'components/Casino/components/CasinoSearch';
import {
  casinoFilterTypeMap,
  deleteCasinoFilter,
  updateCasinoFilter,
} from 'components/Casino/useCasinoFilter';

const ICON_URLS_PATH = `${BASE_PATH}/static/assets/icons/casino`;

const buttonIconName = {
  all_games: 'games',
  favourites: 'casino-star',
  home: 'home',
  search: 'search',
};

const CasinoHomeNavigation = ({
  activeItem,
  isSearchActive,
  loading,
  setActiveItem,
  setIsSearchActive,
  setShowFavorites,
  showFavorites,
}) => {
  const { authenticated } = useApplicationState();
  const { filter, updateFilter } = useCasinoFilterState();
  const navigationType =
    SiteConfigManager.getConfig('config.config.general.desktopCasinoNavigationView') ||
    CASINO_NAVIGATION_TYPES.NAVIGATION1;
  const buttonProperties = SiteConfigManager.getConfig('config.config.buttonProperties');

  const {
    categories: { data: categories },
  } = useCasinoNewState();

  const t = useTranslate();

  const defaultNavigationButtons = useMemo(() => {
    const buttons = authenticated
      ? buttonProperties
      : buttonProperties.filter(button => button.type !== 'favourites');

    return buttons.map(button => {
      const buttonIcon = button.useDefaultIcon
        ? { iconName: buttonIconName[button.type] }
        : { iconUrl: imageUrlCDN(button.icon, CDN_TYPE_CMS) };

      return {
        id: button.type,
        name: `${t(button.type)}`,
        ...buttonIcon,
      };
    });
  }, [authenticated, buttonProperties, t]);

  const items = useMemo(
    () => [
      ...defaultNavigationButtons,
      ...categories
        .sort((a, b) => a.position - b.position)
        .map(({ iconUrl, id, name }) => ({
          iconUrl: iconUrl
            ? encodeURI(imageUrlCDN(iconUrl, CDN_TYPE_GAME))
            : `${ICON_URLS_PATH}/slotMachine.png`,
          id: id.toString(),
          name,
        })),
    ],
    [categories, defaultNavigationButtons],
  );

  useEffect(() => {
    if (!activeItem && categories.length) {
      if (filter.categories.length) {
        const defaultItem = items.find(({ id }) => filter.categories[0].toString() === id) || {
          id: 'home',
        };
        setActiveItem(defaultItem.id);
      } else {
        setActiveItem('home');
      }
    }
  }, [activeItem, items, filter, categories.length]);

  useEffect(() => {
    if (!isSearchActive) {
      if (filter.categories[0]) {
        setActiveItem(`${filter.categories[0]}`);
      } else if (!activeItem) {
        setActiveItem(showFavorites ? 'favourites' : 'home');
      }
    }
  }, [isSearchActive]);

  const onClickHandler = useCallback(item => {
    if (item === 'search') {
      setIsSearchActive(true);
    } else {
      setActiveItem(item);
    }
  }, []);

  useEffect(() => {
    if (!activeItem) {
      return;
    }

    if (['search', 'home', 'all_games'].includes(activeItem)) {
      updateFilter({ categories: [] });
      setShowFavorites(false);

      deleteCasinoFilter(casinoFilterTypeMap.category);
      return;
    }

    if (activeItem === 'favourites') {
      setShowFavorites(true);

      deleteCasinoFilter(casinoFilterTypeMap.category);
      return;
    }

    if (showFavorites) {
      setShowFavorites(false);
      updateFilter({ categories: [activeItem] });
    }

    updateCasinoFilter(casinoFilterTypeMap.category, activeItem);
  }, [activeItem]);

  return (
    <div className="casino-home-navigation">
      {isSearchActive ? (
        <CasinoSearch
          isSearchActive={isSearchActive}
          loading={loading}
          setIsSearchActive={setIsSearchActive}
        />
      ) : (
        <CasinoNavigationMenu
          items={items}
          onClick={onClickHandler}
          selectedItem={activeItem}
          type={navigationType}
          withMoreBtn
        />
      )}
    </div>
  );
};

CasinoHomeNavigation.propTypes = {
  activeItem: PropTypes.string,
  isSearchActive: PropTypes.bool,
  loading: PropTypes.bool,
  setActiveItem: PropTypes.func,
  setIsSearchActive: PropTypes.func,
  setShowFavorites: PropTypes.func,
  showFavorites: PropTypes.bool,
};

export default CasinoHomeNavigation;
